<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import CustomButton from '@/core/elements/CustomButton.vue';
import CustomSeparator from '@/core/elements/CustomSeparator.vue';
import { useCookieConsentStore } from '@/stores/useCookieConsentStore';
import type { ICookieConsentSelection } from '@/utils/cookieConsent.utils';
import CookieConsentCategoryRow from './CookieConsentCategoryRow.vue';
import {
  shouldDisableCookieConsentBannerForPath,
  shouldEnableCookieConsentBanner,
} from './cookieConsentBannerVisibility';

const cookieConsentStore = useCookieConsentStore();
cookieConsentStore.init();
const { shouldShowBanner, hasStoredConsent, settingsOpen, consent } = storeToRefs(cookieConsentStore);
const { t } = useI18n();
const route = useRoute();
const panel = ref<HTMLElement | null>(null);
let settingsTrigger: HTMLElement | null = null;
const isExpanded = ref(false);
const selection = ref<ICookieConsentSelection>({ analytics: false, marketing: false });
const optionalCategories = ['marketing', 'analytics'] as const;
const isBannerEnabled = shouldEnableCookieConsentBanner(import.meta.env);
const shouldRenderBanner = computed(
  () =>
    (isBannerEnabled || settingsOpen.value) &&
    shouldShowBanner.value &&
    !shouldDisableCookieConsentBannerForPath(route.path),
);
const cookiePolicyUrl = 'https://www.digidoo.com/datenschutz-detail';

watch(
  [shouldRenderBanner, settingsOpen],
  async ([visible, openSettings]) => {
    if (!visible) {
      await nextTick();
      if (settingsTrigger?.isConnected) settingsTrigger.focus({ preventScroll: true });
      settingsTrigger = null;
      return;
    }
    isExpanded.value = openSettings;
    selection.value = { analytics: consent.value?.analytics ?? false, marketing: consent.value?.marketing ?? false };
    if (openSettings) {
      settingsTrigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      await nextTick();
      panel.value?.focus({ preventScroll: true });
    }
  },
  { immediate: true },
);
</script>

<template>
  <Transition
    enter-active-class="transition-all duration-200 ease-in-out"
    enter-from-class="translate-y-2 opacity-0"
    enter-to-class="translate-y-0 opacity-100"
    leave-active-class="transition-all duration-200 ease-in-out"
    leave-from-class="translate-y-0 opacity-100"
    leave-to-class="translate-y-2 opacity-0"
  >
    <div
      v-if="shouldRenderBanner"
      ref="panel"
      role="region"
      aria-labelledby="cookie-banner-title"
      tabindex="-1"
      class="fixed inset-x-0 bottom-0 z-[10000] flex justify-center px-0 pb-0 md:px-lg"
    >
      <div
        class="cookie-banner__card w-full max-h-[calc(100dvh-80px)] overflow-hidden rounded-group bg-surface-primary shadow-cookie-banner md:max-h-[100dvh] md:max-w-[960px]"
      >
        <div class="cookie-banner__scroll">
          <div class="flex flex-col gap-lg">
            <div class="flex flex-col gap-lg md:flex-row md:items-center md:gap-xxl">
              <img class="size-icon-xl shrink-0 self-start md:order-2" src="/assets/onboarding/cookie.svg" alt="" />
              <div class="min-w-0 flex-1 md:order-1 md:max-w-[808px]">
                <h2 id="cookie-banner-title" class="text-heading font-semibold leading-[170%] tracking-label">
                  {{ t('cookieConsent.title') }}
                </h2>
                <p class="text-footer leading-[160%] tracking-label md:text-body md:leading-[170%] md:tracking-normal">
                  {{ t('cookieConsent.description') }}
                  <a
                    :href="cookiePolicyUrl"
                    target="_blank"
                    rel="noreferrer"
                    class="font-semibold underline underline-offset-2"
                  >
                    {{ t('cookieConsent.viewCookies') }}
                  </a>
                </p>
              </div>
            </div>
            <template v-if="isExpanded">
              <CustomSeparator />
              <div class="flex flex-col">
                <template v-for="category in optionalCategories" :key="category">
                  <CookieConsentCategoryRow
                    v-model="selection[category]"
                    class="py-lg"
                    :title="t(`cookieConsent.categories.${category}.title`)"
                    :description="t(`cookieConsent.categories.${category}.description`)"
                    :toggle-aria-label="t(`cookieConsent.categories.${category}.ariaLabel`)"
                  />
                  <CustomSeparator />
                </template>
                <CookieConsentCategoryRow
                  class="py-lg"
                  :model-value="true"
                  :title="t('cookieConsent.categories.necessary.title')"
                  :description="t('cookieConsent.categories.necessary.description')"
                  :toggle-aria-label="t('cookieConsent.categories.necessary.ariaLabel')"
                  disabled
                  disabled-theme="success"
                />
              </div>
            </template>
          </div>
        </div>
        <div class="cookie-banner__footer">
          <p v-if="hasStoredConsent" class="mb-smd text-footer">{{ t('cookieConsent.withdrawalHint') }}</p>
          <div class="cookie-banner__actions">
            <CustomButton
              theme="secondary"
              class="cookie-banner__action justify-center !px-[20px] !text-[16px] !leading-[170%] !shadow-none"
              @click="isExpanded ? cookieConsentStore.acceptSelection(selection) : (isExpanded = true)"
            >
              {{ isExpanded ? t('cookieConsent.acceptSelection') : t('cookieConsent.choose') }}
            </CustomButton>
            <CustomButton
              class="cookie-banner__action justify-center !px-[20px] !text-[16px] !leading-[170%] !shadow-none"
              @click="cookieConsentStore.acceptAll()"
            >
              {{ t('cookieConsent.acceptAll') }}
            </CustomButton>
          </div>
          <button
            v-if="hasStoredConsent"
            type="button"
            class="mt-smd text-footer underline"
            @click="cookieConsentStore.closeSettings()"
          >
            {{ t('cookieConsent.close') }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.cookie-banner__card {
  display: flex;
  flex-direction: column;
}

.cookie-banner__scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px clamp(16px, 7vw, 32px) 16px;
}

.cookie-banner__footer {
  flex-shrink: 0;
  padding: 0 clamp(16px, 7vw, 32px) calc(16px + env(safe-area-inset-bottom));
}

.cookie-banner__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cookie-banner__action {
  width: 100%;
}

@media (min-width: 400px) {
  .cookie-banner__actions {
    align-items: center;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
  }
  .cookie-banner__action {
    flex-shrink: 0;
    width: auto;
  }
  .cookie-banner__action:last-child {
    margin-left: auto;
  }
}

@media (min-width: 440px) {
  .cookie-banner__scroll,
  .cookie-banner__footer {
    padding-inline: 32px;
  }
}

@media (min-width: 768px) {
  .cookie-banner__scroll,
  .cookie-banner__footer {
    padding-bottom: 24px;
  }
}
</style>
